import type { UserStateEntry, UserStateItemKey, UserStateMap } from "../../types/user-state";
import { createDefaultUserStateEntry } from "../../types/user-state";
import { normalizeUserStateVersion } from "../../utils/user-state/normalize-user-state-version";
import { isUserStateApiError, type UserStateApiError } from "./userStateApiClient";

export interface UpdateUserStateMutationArgs {
  itemKey: UserStateItemKey;
  data: Record<string, unknown>;
  version: number;
}

export interface DeleteUserStateMutationArgs {
  itemKey: UserStateItemKey;
  version?: number;
}

/**
 * Pure, side-effect-free port of `redux/services/userStateOptimistic.ts`'s optimistic-update /
 * 409-conflict-resolution logic for TanStack Query. Kept framework-free (no `queryClient`, no
 * React) so it can be unit tested directly without rendering a hook — this repo has no
 * renderHook/@testing-library setup (see `hooks/user-state-conflict-resolvers.ts` for the existing
 * precedent of extracting hook logic into plain functions for testability). The mutation hooks in
 * `useUpdateUserStateItem.ts` / `useDeleteUserStateItem.ts` wire these into TanStack Query's
 * `onMutate`/`onError`/`onSuccess` callbacks.
 */

/** True when `error` is a 409 conflict response carrying the server's winning entry. */
export function isUserStateConflictError(
  error: unknown
): error is UserStateApiError & { data: UserStateEntry } {
  return isUserStateApiError(error) && error.status === 409 && error.data !== undefined && error.data !== null;
}

/** Builds the optimistic entry written to the cache before the server responds. */
export function buildOptimisticUserStateEntry(
  data: Record<string, unknown>,
  baseVersion: number
): UserStateEntry {
  return {
    data,
    version: normalizeUserStateVersion(baseVersion) + 1,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Returns a new UserStateMap with `entry` set at `itemKey` (or removed, when `entry` is null) —
 * pure, does not mutate `map`. Used both to apply the optimistic write and to reconcile with the
 * server response / roll back on failure.
 */
export function applyUserStateEntry(
  map: UserStateMap,
  itemKey: UserStateItemKey,
  entry: UserStateEntry | null
): UserStateMap {
  if (entry === null) {
    if (!(itemKey in map)) return map;
    const next = { ...map };
    delete next[itemKey];
    return next;
  }
  return { ...map, [itemKey]: entry };
}

export interface UpdateFailureResolution {
  conflict: boolean;
  /** The entry to write back into the cache for this itemKey. */
  entry: UserStateEntry;
}

/**
 * Resolves what an update mutation should write back into the cache on failure: the server's
 * winning entry on a 409 conflict (so the UI reflects reality instead of silently reverting to a
 * stale local value — matches `handleUserStateUpdateLifecycle`'s conflict branch), or the
 * pre-optimistic entry (falling back to a default empty entry if there wasn't one) for any other
 * failure — matches its non-conflict catch branch.
 */
export function resolveUpdateFailureEntry(
  error: unknown,
  itemKey: UserStateItemKey,
  previousEntry: UserStateEntry | undefined
): UpdateFailureResolution {
  if (isUserStateConflictError(error)) {
    return { conflict: true, entry: error.data };
  }
  return {
    conflict: false,
    entry: previousEntry ?? createDefaultUserStateEntry(itemKey),
  };
}

export interface DeleteFailureResolution {
  conflict: boolean;
  /** The entry to write back for this itemKey, or null to leave it deleted. */
  entry: UserStateEntry | null;
}

/**
 * Resolves what a delete mutation should write back into the cache on failure: the server's
 * winning entry on a 409 (the item was concurrently modified/recreated server-side — matches
 * `handleUserStateDeleteLifecycle`'s conflict branch), the restored pre-optimistic entry on any
 * other failure, or null (stay deleted) if there was no previous entry to restore.
 */
export function resolveDeleteFailureEntry(
  error: unknown,
  previousEntry: UserStateEntry | undefined
): DeleteFailureResolution {
  if (isUserStateConflictError(error)) {
    return { conflict: true, entry: error.data };
  }
  return { conflict: false, entry: previousEntry ?? null };
}

/**
 * Helper mirroring `userStateApi.ts`'s `isUserStateEmpty`: a map is effectively empty if it has
 * no entries, or every entry is an unsaved default (version 0, updatedAt null).
 */
export function isUserStateEmpty(userState: UserStateMap): boolean {
  const entries = Object.keys(userState);
  if (entries.length === 0) return true;
  return entries.every((key) => {
    const entry = userState[key];
    return entry.version === 0 && entry.updatedAt === null;
  });
}
